import { useDispatch } from "react-redux";
import { goToShift } from "../../../providers/GlobalStateProvider";
import { getShiftIndex } from "./steppable-range-selectors/ShiftSelector/shiftUtils";
import { ONE_HOUR_OF_MS } from "../../../utils/timeUtils";
import { useSettings } from "../../../contexts/SettingsManager/SettingsManager";
import {
  useInterval,
  useShouldMockShiftEnd,
  useShifts,
  useSelectedShift,
  useFocusRange,
  useDashboardPageContext,
  useFormatZoned,
} from "../../../utils/customHooks/genericHooks";
import { AUTO_NEXT_SHIFT_DELAY } from "../../../constants/intervals";
import { isEqual } from "lodash";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { useTransition } from "react";

// if focusRange.ge is more than this time into the past, stay in the past
// else always return to current shift
const MAX_TIME_AGO_FOR_LIVE_MODE = 23 * ONE_HOUR_OF_MS;

// time until next shift starts when on url ?mockshift=end
const MOCK_SHIFT_END_DELAY = 5 * 1000;

/** Ensures that we go to the current shift on shift change,
 * UNLESS the user is looking at focusRange.ge > 23 hours into the past
 */
const StayOnCurrentShift = () => {
  const dispatch = useDispatch();
  const shifts = useShifts();
  const selectedShift = useSelectedShift();
  const focusRange = useFocusRange();
  const formatZoned = useFormatZoned();

  const { isFullDaySelected, setIsFullDaySelected } = useDashboardPageContext();
  const { dayStartMsOffset, shouldStayOnCurrentShift } = useSettings();
  const shouldMockShiftEnd = useShouldMockShiftEnd();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const [, startTransition] = useTransition();

  // const dayDuration = (lt - ge) / ONE_HOUR_OF_MS;
  // const timeChangeAlert = dayDuration === 24 ? "" : "Time change! ";

  // Regularly check if we should move from the currently
  // selected shift to the shift that is currently happening
  const checkCurrentTimeAndGoToCurrentShift = () => {
    const now = Date.now();

    if (shifts) {
      const nextIndex = getShiftIndex(shifts, now);

      // if the shift was found
      if (nextIndex >= 0) {
        const nextShift = shifts[nextIndex];
        const currentShift = shifts[nextIndex - 1];

        // if the next shift was found
        if (nextShift?.range.ge && nextShift?.range.lt) {
          const selectedShiftIndex = getShiftIndex(
            shifts,
            selectedShift.range.ge
          );

          const isNextShiftYet = nextIndex > selectedShiftIndex;
          const nextGe = isNextShiftYet
            ? +nextShift.range.ge
            : +nextShift.range.lt;

          const isFarEnoughIntoNextShift =
            nextGe &&
            now - nextGe >
              (shouldMockShiftEnd
                ? MOCK_SHIFT_END_DELAY
                : AUTO_NEXT_SHIFT_DELAY);

          // auto-advance if we're within 23 hours of now
          const minDateForAutoNextShift = now - MAX_TIME_AGO_FOR_LIVE_MODE;

          // for extra-long shifts e.g. 3-day weekend shifts with duration > 23h,
          // check also if we're looking at the current shift
          const isLookingAtCurrentShift = isEqual(selectedShift, currentShift);

          // keep track of current shift if we're looking within the last N hours
          const isLookingAtRecentData =
            (focusRange && focusRange.ge >= minDateForAutoNextShift) ||
            // also advance if we're >23h in the past, but still on the current shift
            isLookingAtCurrentShift;

          const shouldGoToNextShift =
            isFarEnoughIntoNextShift && isLookingAtRecentData;
          startTransition(() => {
            if (shouldGoToNextShift) {
              console.log(
                "checkCurrentTimeAndGoToCurrentShift: Set range selection to next/current shift"
              );
              dispatch(
                goToShift(
                  nextShift,
                  dayStartMsOffset,
                  timeZoneMsOffset,
                  formatZoned
                )
              );
              if (isFullDaySelected) {
                setIsFullDaySelected(false);
              }
            }
          });
        }
      }
    }
  };

  useInterval({
    callback: checkCurrentTimeAndGoToCurrentShift,
    // check every 2.5 minutes whether we should move to the next shift
    // (check every 15 seconds when mocking an ending shift)
    delay: !shouldStayOnCurrentShift
      ? null
      : shouldMockShiftEnd
        ? MOCK_SHIFT_END_DELAY
        : 0.5 * AUTO_NEXT_SHIFT_DELAY,
    immediate: false,
  });

  return null;
};
export default StayOnCurrentShift;
